import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../services/api';
import { format, formatDistanceToNow } from 'date-fns';

interface UserActivity {
  userId: string;
  email: string;
  fullName: string;
  lastLoginAt: string;
  updatedAt: string;
}

export default function UserActivityMonitor() {
  const queryClient = useQueryClient();
  const [isSyncing, setIsSyncing] = useState(false);
  const [syncStatus, setSyncStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [syncMessage, setSyncMessage] = useState('');

  const handleSyncUsers = async () => {
    setIsSyncing(true);
    setSyncStatus('idle');
    setSyncMessage('');
    
    try {
      const result = await api.syncUsersFromCognito();
      setSyncStatus('success');
      setSyncMessage(`✅ Synced ${result.syncedToDatabase} users from Cognito`);
      
      // Refresh the activity data
      await queryClient.invalidateQueries({ queryKey: ['user-login-activity'] });
      
      // Auto-clear message after 5 seconds
      setTimeout(() => setSyncStatus('idle'), 5000);
    } catch (err) {
      setSyncStatus('error');
      setSyncMessage(`❌ Sync failed: ${err instanceof Error ? err.message : 'Unknown error'}`);
      setTimeout(() => setSyncStatus('idle'), 5000);
    } finally {
      setIsSyncing(false);
    }
  };

  const { data, isLoading, error } = useQuery({
    queryKey: ['user-login-activity'],
    queryFn: () => api.getLoginActivity(),
    refetchInterval: 60 * 1000, // Refresh every minute
    staleTime: 30 * 1000, // Consider data stale after 30 seconds
  });

  if (isLoading) {
    return (
      <div className="bg-white rounded-2xl shadow-xl p-6">
        <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center gap-2">
          <span>📍</span>
          User Login Activity
        </h2>
        <div className="flex justify-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-2xl p-6">
        <h2 className="text-2xl font-bold text-red-900 mb-2 flex items-center gap-2">
          <span>⚠️</span>
          Error Loading Activity
        </h2>
        <p className="text-red-700">{(error as Error).message}</p>
      </div>
    );
  }

  const activities: UserActivity[] = data?.users || [];

  // Calculate stats
  const activeLast7Days = activities.filter(u => {
    if (u.lastLoginAt === 'Never') return false;
    const lastLogin = new Date(u.lastLoginAt);
    const now = new Date();
    const daysDiff = (now.getTime() - lastLogin.getTime()) / (1000 * 60 * 60 * 24);
    return daysDiff <= 7;
  }).length;

  const activeLast30Days = activities.filter(u => {
    if (u.lastLoginAt === 'Never') return false;
    const lastLogin = new Date(u.lastLoginAt);
    const now = new Date();
    const daysDiff = (now.getTime() - lastLogin.getTime()) / (1000 * 60 * 60 * 24);
    return daysDiff <= 30;
  }).length;

  // Inactive > 30 days
  const inactiveLongTerm = activities.filter(u => {
    if (u.lastLoginAt === 'Never') return false;
    const lastLogin = new Date(u.lastLoginAt);
    const now = new Date();
    const daysDiff = (now.getTime() - lastLogin.getTime()) / (1000 * 60 * 60 * 24);
    return daysDiff > 30;
  }).length;

  // Average days since login
  const daysLoginList = activities
    .filter(u => u.lastLoginAt !== 'Never')
    .map(u => {
      const lastLogin = new Date(u.lastLoginAt);
      const now = new Date();
      return (now.getTime() - lastLogin.getTime()) / (1000 * 60 * 60 * 24);
    });
  const averageDaysSinceLogin = daysLoginList.length > 0 
    ? (daysLoginList.reduce((a, b) => a + b, 0) / daysLoginList.length).toFixed(1)
    : 'N/A';

  // Churn rate: % of users inactive > 30 days
  const churnRate = activities.length > 0
    ? ((inactiveLongTerm / activities.length) * 100).toFixed(1)
    : 'N/A';

  return (
    <div className="space-y-6">
      {/* Stats Cards */}
      <div className="grid md:grid-cols-4 gap-4">
        <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-xl p-6 border border-green-200">
          <div className="text-sm font-semibold text-green-700 mb-1">Active Last 7 Days</div>
          <div className="text-3xl font-bold text-green-900">{activeLast7Days}</div>
          <div className="text-xs text-green-600 mt-2">Regular users</div>
        </div>
        
        <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-xl p-6 border border-blue-200">
          <div className="text-sm font-semibold text-blue-700 mb-1">Active Last 30 Days</div>
          <div className="text-3xl font-bold text-blue-900">{activeLast30Days}</div>
          <div className="text-xs text-blue-600 mt-2">Monthly active users</div>
        </div>
        
        <div className="bg-gradient-to-br from-yellow-50 to-yellow-100 rounded-xl p-6 border border-yellow-200">
          <div className="text-sm font-semibold text-yellow-700 mb-1">Total Users</div>
          <div className="text-3xl font-bold text-yellow-900">{activities.length}</div>
          <div className="text-xs text-yellow-600 mt-2">All registered users</div>
        </div>
        
        <div className="bg-gradient-to-br from-orange-50 to-orange-100 rounded-xl p-6 border border-orange-200">
          <div className="text-sm font-semibold text-orange-700 mb-1">Inactive &gt; 30 Days</div>
          <div className="text-3xl font-bold text-orange-900">{inactiveLongTerm}</div>
          <div className="text-xs text-orange-600 mt-2">Users with no recent activity</div>
        </div>
      </div>

      {/* Engagement Health Section */}
      <div className="grid md:grid-cols-2 gap-4">
        <div className="bg-gradient-to-br from-purple-50 to-purple-100 rounded-xl p-6 border border-purple-200">
          <div className="text-sm font-semibold text-purple-700 mb-3">📊 Average Days Since Login</div>
          <div className="text-4xl font-bold text-purple-900">{averageDaysSinceLogin}</div>
          <div className="text-xs text-purple-600 mt-2">
            {averageDaysSinceLogin === 'N/A' 
              ? 'No login data available' 
              : 'days - How "stale" is the user base?'}
          </div>
          <div className="mt-3 pt-3 border-t border-purple-200 text-xs text-purple-700">
            Lower is better: Indicates users are logging in more frequently
          </div>
        </div>

        <div className="bg-gradient-to-br from-red-50 to-red-100 rounded-xl p-6 border border-red-200">
          <div className="text-sm font-semibold text-red-700 mb-3">⚠️ Churn Rate</div>
          <div className="text-4xl font-bold text-red-900">{churnRate}%</div>
          <div className="text-xs text-red-600 mt-2">
            {churnRate === 'N/A' 
              ? 'No data available' 
              : 'of users inactive 30+ days'}
          </div>
          <div className="mt-3 pt-3 border-t border-red-200 text-xs text-red-700">
            Lower is better: Less than 20% indicates healthy engagement
          </div>
        </div>
      </div>

      {/* Sync Users Section */}
      {activities.length === 0 && (
        <div className="bg-amber-50 border-2 border-amber-200 rounded-2xl p-6">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-lg font-bold text-amber-900 mb-2">No User Data Yet</h3>
              <p className="text-amber-700">Sync registered users from Cognito to populate the activity dashboard.</p>
            </div>
            <button
              onClick={handleSyncUsers}
              disabled={isSyncing}
              className="px-6 py-3 bg-amber-600 hover:bg-amber-700 disabled:bg-amber-400 text-white font-semibold rounded-lg transition-colors whitespace-nowrap ml-4"
            >
              {isSyncing ? '⏳ Syncing...' : '🔄 Sync Users'}
            </button>
          </div>
          {syncStatus !== 'idle' && (
            <div className={`mt-4 p-3 rounded-lg ${syncStatus === 'success' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
              {syncMessage}
            </div>
          )}
        </div>
      )}

      {/* Activity Table */}
      <div className="bg-white rounded-2xl shadow-xl p-6">
        <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center gap-2">
          <span>📍</span>
          User Login Activity
        </h2>
        
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b-2 border-gray-200">
                <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">User</th>
                <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Email</th>
                <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Last Login</th>
                <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Time Since Login</th>
                <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Status</th>
              </tr>
            </thead>
            <tbody>
              {activities.map((user) => {
                const lastLoginDate = user.lastLoginAt === 'Never' ? null : new Date(user.lastLoginAt);
                const isActiveLast7Days = lastLoginDate && (new Date().getTime() - lastLoginDate.getTime()) / (1000 * 60 * 60 * 24) <= 7;
                const isActiveLast30Days = lastLoginDate && (new Date().getTime() - lastLoginDate.getTime()) / (1000 * 60 * 60 * 24) <= 30;
                
                return (
                  <tr key={user.userId} className="border-b border-gray-100 hover:bg-gray-50 transition-colors">
                    <td className="py-3 px-4">
                      <div className="font-medium text-gray-900">{user.fullName}</div>
                    </td>
                    <td className="py-3 px-4 text-sm text-gray-600">{user.email}</td>
                    <td className="py-3 px-4 text-sm text-gray-600">
                      {user.lastLoginAt === 'Never' ? (
                        <span className="text-red-600 font-medium">Never</span>
                      ) : (
                        <>
                          <div>{format(lastLoginDate!, 'MMM dd, yyyy')}</div>
                          <div className="text-xs text-gray-400">{format(lastLoginDate!, 'HH:mm')}</div>
                        </>
                      )}
                    </td>
                    <td className="py-3 px-4 text-sm text-gray-600">
                      {user.lastLoginAt === 'Never' ? (
                        <span className="text-red-600">-</span>
                      ) : (
                        <span>{formatDistanceToNow(lastLoginDate!, { addSuffix: true })}</span>
                      )}
                    </td>
                    <td className="py-3 px-4">
                      {user.lastLoginAt === 'Never' ? (
                        <span className="inline-block px-3 py-1 rounded-full text-xs font-semibold bg-red-100 text-red-800">
                          Never logged in
                        </span>
                      ) : isActiveLast7Days ? (
                        <span className="inline-block px-3 py-1 rounded-full text-xs font-semibold bg-green-100 text-green-800">
                          Active
                        </span>
                      ) : isActiveLast30Days ? (
                        <span className="inline-block px-3 py-1 rounded-full text-xs font-semibold bg-blue-100 text-blue-800">
                          Somewhat active
                        </span>
                      ) : (
                        <span className="inline-block px-3 py-1 rounded-full text-xs font-semibold bg-yellow-100 text-yellow-800">
                          Inactive
                        </span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {activities.length === 0 && (
          <div className="text-center py-8">
            <div className="text-gray-400 text-lg">No user activity data available yet</div>
          </div>
        )}
      </div>

      {/* Top & Bottom Users Comparison */}
      {activities.filter(u => u.lastLoginAt !== 'Never').length > 0 && (
        <div className="grid md:grid-cols-2 gap-6">
          {/* Top 5 Most Active Users */}
          <div className="bg-white rounded-2xl shadow-xl p-6">
            <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center gap-2">
              <span>🏆</span>
              Top 5 Most Active
            </h2>
            <div className="space-y-3">
              {activities
                .filter(u => u.lastLoginAt !== 'Never')
                .sort((a, b) => {
                  const aLastLogin = new Date(a.lastLoginAt).getTime();
                  const bLastLogin = new Date(b.lastLoginAt).getTime();
                  return bLastLogin - aLastLogin;
                })
                .slice(0, 5)
                .map((user, idx) => {
                  const lastLoginDate = new Date(user.lastLoginAt);
                  const daysSince = (new Date().getTime() - lastLoginDate.getTime()) / (1000 * 60 * 60 * 24);
                  return (
                    <div key={user.userId} className="flex items-center justify-between p-3 bg-green-50 rounded-lg border border-green-200">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-full bg-green-600 text-white flex items-center justify-center text-sm font-bold">
                          {idx + 1}
                        </div>
                        <div>
                          <div className="font-medium text-gray-900">{user.fullName}</div>
                          <div className="text-xs text-gray-500">{user.email}</div>
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="text-sm font-semibold text-green-700">{daysSince.toFixed(1)} days ago</div>
                        <div className="text-xs text-green-600">{format(lastLoginDate, 'MMM dd')}</div>
                      </div>
                    </div>
                  );
                })}
            </div>
          </div>

          {/* Bottom 5 Least Active Users */}
          <div className="bg-white rounded-2xl shadow-xl p-6">
            <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center gap-2">
              <span>📉</span>
              Bottom 5 Least Active
            </h2>
            <div className="space-y-3">
              {activities
                .filter(u => u.lastLoginAt !== 'Never')
                .sort((a, b) => {
                  const aLastLogin = new Date(a.lastLoginAt).getTime();
                  const bLastLogin = new Date(b.lastLoginAt).getTime();
                  return aLastLogin - bLastLogin;
                })
                .slice(0, 5)
                .map((user, idx) => {
                  const lastLoginDate = new Date(user.lastLoginAt);
                  const daysSince = (new Date().getTime() - lastLoginDate.getTime()) / (1000 * 60 * 60 * 24);
                  return (
                    <div key={user.userId} className="flex items-center justify-between p-3 bg-red-50 rounded-lg border border-red-200">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-full bg-red-600 text-white flex items-center justify-center text-sm font-bold">
                          {idx + 1}
                        </div>
                        <div>
                          <div className="font-medium text-gray-900">{user.fullName}</div>
                          <div className="text-xs text-gray-500">{user.email}</div>
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="text-sm font-semibold text-red-700">{daysSince.toFixed(1)} days ago</div>
                        <div className="text-xs text-red-600">{format(lastLoginDate, 'MMM dd')}</div>
                      </div>
                    </div>
                  );
                })}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

